/**
 * InvoiceConfirmCard — Rich generative UI card for invoice write-action confirms (P2b).
 *
 * Props:
 *   msg          — the confirmation UI message (facts narrowed via isInvoiceConfirmFacts)
 *   onApprove    — callback for Freigeben (no note needed)
 *   onReject     — callback for Ablehnen (note: string, ≥10 chars — enforced in UI + server)
 *   onRueckfrage — callback for Rückfrage stellen (note: string, ≥10 chars)
 *
 * Layout:
 *   Header: Kunde · Versicherer (neutral text, no colour)
 *   Betrag row: CHF {betrag} — only when facts.betrag is present (finance-gated)
 *   Differenz row: Badge — the ONLY coloured element:
 *       Number(differenz) !== 0 → variant="alert" (semantic alert colour)
 *       Number(differenz) === 0 → variant="neutral"
 *   "Details ansehen" — collapsible expander revealing invoiceNumber, carrierUid,
 *       currentStatus, decision as a neutral <dl>. Collapsed by default.
 *   Action row (pending only):
 *       Freigeben (default)      → onApprove()
 *       Rückfrage (outline)      → reveals textarea → disabled until note ≥ 10 chars
 *       Ablehnen  (outline)      → reveals textarea → disabled until note ≥ 10 chars
 *   Resolved state: neutral one-liner text (no buttons).
 *
 * Colour discipline (locked): ONLY the Differenz badge carries colour.
 *
 * [Source: Story P2b spec §3.4]
 */

import { useState } from 'react';
import type { BrokUiMessage } from '../brok-chat.types';
import { isInvoiceConfirmFacts } from '@brokly/shared';
import type { InvoiceConfirmFacts } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';

type ConfirmMsg = Extract<BrokUiMessage, { kind: 'confirmation' }>;

interface Props {
  msg: ConfirmMsg;
  onApprove: (toolCallId: string) => void;
  onReject: (toolCallId: string, note: string) => void;
  onRueckfrage: (toolCallId: string, note: string) => void;
}

const MIN_NOTE_LEN = 10;

export function InvoiceConfirmCard({ msg, onApprove, onReject, onRueckfrage }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [activeAction, setActiveAction] = useState<'reject' | 'rueckfrage' | null>(null);
  const [note, setNote] = useState('');

  const isPending = msg.decision === 'pending';

  // Narrow the facts to InvoiceConfirmFacts (caller should have already checked,
  // but guard defensively so the component never crashes on stale data).
  if (!isInvoiceConfirmFacts(msg.facts)) return null;

  const facts: InvoiceConfirmFacts = msg.facts;

  // ── Differenz badge variant (the ONLY coloured element) ────────────────────
  const differenzNum = facts.differenz != null ? Number(facts.differenz) : 0;
  const differenzVariant =
    facts.differenz != null && differenzNum !== 0 ? 'alert' : 'neutral';

  // ── Resolved state ─────────────────────────────────────────────────────────
  const resolvedLabel: Record<string, string> = {
    approved: 'Freigegeben.',
    rejected: 'Abgelehnt.',
    rueckfrage: 'Rückfrage notiert.',
    failed: 'Aktion fehlgeschlagen.',
  };

  const handleNoteSubmit = () => {
    if (note.trim().length < MIN_NOTE_LEN) return;
    if (activeAction === 'reject') {
      onReject(msg.toolCallId, note.trim());
    } else if (activeAction === 'rueckfrage') {
      onRueckfrage(msg.toolCallId, note.trim());
    }
  };

  return (
    <Card className="p-3 text-sm space-y-2">
      {/* ── Header row: Kunde · Versicherer ────────────────────────────────── */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        <span className="text-foreground font-medium">{facts.kundeName}</span>
        <span className="text-muted-foreground text-xs self-center">·</span>
        <span className="text-muted-foreground">{facts.versichererName}</span>
      </div>

      {/* ── Summary line ────────────────────────────────────────────────────── */}
      <p className="text-foreground">{msg.summary}</p>

      {/* ── Finance-gated rows ───────────────────────────────────────────────── */}
      {facts.betrag != null && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Betrag</span>
          <span className="text-sm mono-num">CHF {facts.betrag}</span>
        </div>
      )}

      {facts.differenz != null && (
        <div className="flex items-center gap-2" data-testid="differenz-row">
          <span className="text-xs text-muted-foreground">Differenz</span>
          <Badge variant={differenzVariant} data-testid="differenz-badge">
            {differenzNum > 0 ? '+' : ''}{facts.differenz}
          </Badge>
        </div>
      )}

      {/* ── "Details ansehen" expander ──────────────────────────────────────── */}
      <div>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Details verbergen' : 'Details ansehen'}
        </button>
        {expanded && (
          <dl className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            <div className="flex gap-1">
              <dt className="font-medium">Rechnungsnummer:</dt>
              <dd>{facts.invoiceNumber}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="font-medium">Versicherer-UID:</dt>
              <dd>{facts.carrierUid}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="font-medium">Status:</dt>
              <dd>{facts.currentStatus}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="font-medium">Entscheidung:</dt>
              <dd>{facts.decision}</dd>
            </div>
          </dl>
        )}
      </div>

      {/* ── Action area ─────────────────────────────────────────────────────── */}
      {isPending ? (
        <div className="space-y-2">
          {/* Inline note textarea (shown when Ablehnen or Rückfrage is active) */}
          {activeAction != null && (
            <div className="space-y-1">
              <textarea
                className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                rows={2}
                placeholder={
                  activeAction === 'reject'
                    ? 'Ablehnungsgrund (mind. 10 Zeichen) …'
                    : 'Rückfrage (mind. 10 Zeichen) …'
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
                aria-label={activeAction === 'reject' ? 'Ablehnungsgrund' : 'Rückfragetext'}
              />
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="default"
                  disabled={note.trim().length < MIN_NOTE_LEN}
                  onClick={handleNoteSubmit}
                >
                  {activeAction === 'reject' ? 'Ablehnen bestätigen' : 'Rückfrage senden'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setActiveAction(null); setNote(''); }}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}

          {/* Primary action buttons */}
          {activeAction == null && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => onApprove(msg.toolCallId)}
              >
                Freigeben
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setActiveAction('rueckfrage'); setNote(''); }}
              >
                Rückfrage
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setActiveAction('reject'); setNote(''); }}
              >
                Ablehnen
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {resolvedLabel[msg.decision] ?? msg.decision}
        </p>
      )}
    </Card>
  );
}
